import React from 'react';
import { useTheme } from '../../theme/ThemeProvider';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Suspense } from 'react';

// The full markdown content for the Vendor API Guide - using string concatenation to avoid TS issues
const GUIDE_CONTENT = 
"# Vendor API Guide\n\n" +
"This guide provides documentation for interacting with our vendor API portal, allowing you to submit test results, retrieve reports, and manage access to our services.\n\n" +
"## API Endpoints\n\n" +
"### Authentication\n\n" +
"```http\n" +
"POST /api/v1/auth/validate\n" +
"```\n\n" +
"This endpoint allows vendors to validate their API credentials. We support two authentication methods:\n\n" +
"**Method 1: API Key Authentication (Recommended)**\n\n" +
"```http\n" +
"Authorization: Bearer {api_key}\n" +
"```\n\n" +
"**Method 2: Webhook Signature Authentication**\n\n" +
"Generate an HMAC-SHA256 signature using your webhook secret:\n\n" +
"```http\n" +
"X-Vendor-Signature: {hmac_signature}\n" +
"```\n\n" +
"**Example Signature Generation (Python):**\n" +
"```python\n" +
"import hmac\n" +
"import hashlib\n" +
"import json\n\n" +
"def generate_signature(payload, secret):\n" +
"    payload_bytes = json.dumps(payload).encode('utf-8')\n" +
"    signature = hmac.new(\n" +
"        secret.encode('utf-8'),\n" +
"        payload_bytes,\n" +
"        hashlib.sha256\n" +
"    ).hexdigest()\n" +
"    return signature\n" +
"```\n\n" +
"**Response:**\n" +
"```json\n" +
"{\n" +
'  "status": "valid",\n' +
'  "vendor_id": "VENDOR_123",\n' +
'  "message": "API credentials are valid"\n' +
"}\n" +
"```\n\n" +
"### Submit Test Results\n\n" +
"```http\n" +
"POST /api/v1/vendor/test-results\n" +
"```\n\n" +
"This endpoint allows vendors to submit test results for processing.\n\n" +
"**Request Headers:**\n" +
"For API Key Authentication (Recommended):\n" +
"```\n" +
"Authorization: Bearer {api_key}\n" +
"Content-Type: application/json\n" +
"```\n\n" +
"For Webhook Signature Authentication:\n" +
"```\n" +
"X-Vendor-Signature: {hmac_signature}\n" +
"Content-Type: application/json\n" +
"```\n\n" +
"**Request Body:**\n" +
"```json\n" +
"{\n" +
'  "vendor_id": "VENDOR_123",\n' +
'  "timestamp": "2024-09-15T14:30:25.123Z",\n' +
'  "request_id": "req-97e3d2a1-b456-7890-c123-d4e5f6a7b8c9",\n' +
'  "test_data": {\n' +
'    "test_id": "159809",\n' +
'    "panel_type": "RESPIRATORY",\n' +
'    "panel_size": 56,\n' +
'    "patient_info": {\n' +
'      "patient_id": "56012",\n' +
'      "patient_name": "Mcdaniel, Garrick",\n' +
'      "patient_dob": "1995-01-06",\n' +
'      "patient_gender": "Male",\n' +
'      "patient_age": "29 years"\n' +
"    },\n" +
'    "collection_info": {\n' +
'      "collection_site": "NASO",\n' +
'      "specimen_type": "Nasopharyngeal swab",\n' +
'      "collection_date": "2024-09-10T13:11:00-06:00",\n' +
'      "received_date": "2024-09-11T10:55:00-06:00"\n' +
"    },\n" +
'    "provider_info": {\n' +
'      "provider_name": "Patel, Satish",\n' +
'      "provider_location": "Main Clinic"\n' +
"    },\n" +
'    "test_details": {\n' +
'      "viruses": [\n' +
"        {\n" +
'          "name": "SARS-CoV-2 (N gene)",\n' +
'          "result": "Not Detected"\n' +
"        }\n" +
"      ],\n" +
'      "bacteria": [\n' +
"        {\n" +
'          "name": "Bordetella pertussis",\n' +
'          "result": "Not Detected"\n' +
"        }\n" +
"      ],\n" +
'      "resistance_genes": [\n' +
"        {\n" +
'          "name": "beta-lactamase CTX-M",\n' +
'          "result": "Detected"\n' +
"        }\n" +
"      ],\n" +
'      "fungi": [\n' +
"        {\n" +
'          "name": "Candida albicans",\n' +
'          "result": "Not Detected"\n' +
"        }\n" +
"      ]\n" +
"    },\n" +
'    "metadata": {\n' +
'      "report_status": "Final",\n' +
'      "report_date": "2024-09-12T09:18:00-06:00",\n' +
'      "performing_lab": "Main Laboratory",\n' +
'      "test_method": "PCR"\n' +
"    }\n" +
"  }\n" +
"}\n" +
"```\n\n" +
"**Response:**\n" +
"```json\n" +
"{\n" +
'  "success": true,\n' +
'  "message": "Test result submitted successfully",\n' +
'  "report_id": "RPT_123456"\n' +
"}\n" +
"```\n\n" +
"### Retrieve Reports\n\n" +
"```http\n" +
"GET /api/v1/vendor/reports/{report_id}\n" +
"```\n\n" +
"This endpoint allows vendors to retrieve a report by ID.\n\n" +
"**Request Headers:**\n" +
"For API Key Authentication (Recommended):\n" +
"```\n" +
"Authorization: Bearer {api_key}\n" +
"```\n\n" +
"For Webhook Signature Authentication:\n" +
"```\n" +
"X-Vendor-Signature: {hmac_signature}\n" +
"```\n\n" +
"**Response:**\n" +
"```json\n" +
"{\n" +
'  "success": true,\n' +
'  "message": "Report found",\n' +
'  "report_id": "RPT_123456",\n' +
'  "test_id": "TEST_456",\n' +
'  "status": "Completed",\n' +
'  "created_at": "2023-06-15T10:30:00Z",\n' +
'  "pdf_url": "/api/v1/vendor/pdf/VENDOR_123/RPT_123456"\n' +
"}\n" +
"```\n\n" +
"### Retrieve Reports\n\n" +
"```http\n" +
"GET /api/v1/lis-webhook/vendor/report/{data_id}\n" +
"```\n\n" +
"This endpoint allows vendors to retrieve report information for a specific data ID.\n\n" +
"**Request Headers:**\n" +
"For API Key Authentication (Recommended):\n" +
"```\n" +
"Authorization: Bearer {api_key}\n" +
"```\n\n" +
"For Webhook Signature Authentication:\n" +
"```\n" +
"X-Vendor-Signature: {hmac_signature}\n" +
"```\n\n" +
"**Response:**\n" +
"```json\n" +
"{\n" +
'  "success": true,\n' +
'  "message": "Report found",\n' +
'  "data_id": "DATA_123456",\n' +
'  "report_id": "RPT_123456",\n' +
'  "test_id": "TEST_456",\n' +
'  "status": "Completed",\n' +
'  "created_at": "2023-06-15T10:30:00Z",\n' +
'  "pdf_url": "/api/v1/lis-webhook/vendor/pdf/VENDOR_123/DATA_123456"\n' +
"}\n" +
"```\n\n" +
"### Download PDF Report\n\n" +
"```http\n" +
"GET /api/v1/vendor/pdf/{vendor_id}/{report_id}\n" +
"```\n\n" +
"This endpoint allows vendors to download the PDF report generated for a specific report ID.\n\n" +
"**Request Headers:**\n" +
"For API Key Authentication (Recommended):\n" +
"```\n" +
"Authorization: Bearer {api_key}\n" +
"```\n\n" +
"For Webhook Signature Authentication:\n" +
"```\n" +
"X-Vendor-Signature: {hmac_signature}\n" +
"```\n\n" +
"**Response:**\n" +
"The response is a PDF file with appropriate content type (`application/pdf`). The file can be saved locally or displayed in a browser.\n\n" +
"### Download PDF Report\n\n" +
"```http\n" +
"GET /api/v1/lis-webhook/vendor/pdf/{vendor_id}/{data_id}\n" +
"```\n\n" +
"This endpoint allows vendors to download the PDF report generated for a specific data ID. The PDF URL is typically provided in the report and status responses.\n\n" +
"**Request Headers:**\n" +
"For API Key Authentication (Recommended):\n" +
"```\n" +
"Authorization: Bearer {api_key}\n" +
"```\n\n" +
"For Webhook Signature Authentication:\n" +
"```\n" +
"X-Vendor-Signature: {hmac_signature}\n" +
"```\n\n" +
"**Response:**\n" +
"The response is a binary PDF file with content type `application/pdf`. The file can be saved locally or displayed in a browser.\n\n" +
"**Important Notes:**\n" +
"- The PDF URL may be provided as a relative URL (starting with `/`), which needs to be resolved against your base API URL\n" +
"- The PDF is only available after the report status is `completed`\n" +
"- Always stream the download using `stream=True` parameter for large files\n\n" +
"### Check Report Status\n\n" +
"```http\n" +
"GET /api/v1/vendor/status/{data_id}\n" +
"```\n\n" +
"This endpoint allows vendors to check the processing status of a submitted report.\n\n" +
"**Request Headers:**\n" +
"For API Key Authentication (Recommended):\n" +
"```\n" +
"Authorization: Bearer {api_key}\n" +
"```\n\n" +
"For Webhook Signature Authentication:\n" +
"```\n" +
"X-Vendor-Signature: {hmac_signature}\n" +
"```\n\n" +
"**Response:**\n" +
"```json\n" +
"{\n" +
'  "data_id": "DATA_789",\n' +
'  "status": "completed",\n' +
'  "message": "Processing completed successfully",\n' +
'  "pdf_url": "/api/v1/vendor/pdf/VENDOR_123/DATA_789"\n' +
"}\n" +
"```\n\n" +
"Possible status values:\n" +
"- `pending`: Data is received and waiting to be processed\n" +
"- `processing`: Data is currently being processed\n" +
"- `completed`: Processing is complete and report is available\n" +
"- `failed`: Processing failed, see message for details\n\n" +
"### Check Report Status\n\n" +
"```http\n" +
"GET /api/v1/lis-webhook/vendor/status/{data_id}\n" +
"```\n\n" +
"This endpoint allows vendors to check the processing status of submitted data.\n\n" +
"**Request Headers:**\n" +
"For API Key Authentication (Recommended):\n" +
"```\n" +
"Authorization: Bearer {api_key}\n" +
"```\n\n" +
"For Webhook Signature Authentication:\n" +
"```\n" +
"X-Vendor-Signature: {hmac_signature}\n" +
"```\n\n" +
"**Response:**\n" +
"```json\n" +
"{\n" +
'  "data_id": "DATA_789",\n' +
'  "status": "completed",\n' +
'  "message": "Processing completed successfully",\n' +
'  "pdf_url": "/api/v1/lis-webhook/vendor/pdf/VENDOR_123/DATA_789"\n' +
"}\n" +
"```\n\n" +
"**Possible status values:**\n" +
"- `pending`: Data is received and waiting to be processed\n" +
"- `processing`: Data is currently being processed\n" +
"- `completed`: Processing is complete and report is available\n" +
"- `failed`: Processing failed, see message for details\n\n" +
"**Workflow for handling reports:**\n" +
"1. Submit data using the submit-data endpoint\n" +
"2. Periodically check the status endpoint until status is `completed`\n" +
"3. Once completed, retrieve the report metadata using the report endpoint\n" +
"4. Use the `pdf_url` from either the status or report response to download the PDF\n" +
"5. Process the PDF report as needed for your application\n\n" +
"## Authentication\n\n" +
"Our API supports two authentication methods:\n\n" +
"### Method 1: API Key Authentication (Recommended)\n\n" +
"The API uses bearer token authentication with your assigned API key:\n\n" +
"1. Include your API key in the Authorization header of your requests\n" +
"2. Format: `Authorization: Bearer {api_key}`\n" +
"3. API keys don't expire but can be revoked or regenerated in the vendor portal\n\n" +
"### Method 2: Webhook Signature Authentication\n\n" +
"For webhook integrations, you can validate requests using HMAC signatures:\n\n" +
"1. Generate an HMAC-SHA256 signature using your webhook secret key and the request payload\n" +
"2. Include the signature in the X-Vendor-Signature header\n" +
"3. Our server will verify the signature matches what's expected based on your webhook secret\n\n" +
"For enhanced security, include a timestamp in your payload and verify recent timestamps on the receiving end.\n\n" +
"## Error Handling\n\n" +
"The API uses standard HTTP status codes:\n\n" +
"- 200: Success\n" +
"- 400: Bad Request (invalid data)\n" +
"- 401: Unauthorized (invalid or expired token)\n" +
"- 403: Forbidden (insufficient permissions)\n" +
"- 404: Not Found (resource doesn't exist)\n" +
"- 500: Server Error\n\n" +
"Error responses include a message explaining the issue:\n\n" +
"```json\n" +
"{\n" +
'  "error": true,\n' +
'  "message": "Invalid test data format",\n' +
'  "details": "The \'panel_type\' field is required"\n' +
"}\n" +
"```\n\n" +
"## Rate Limiting\n\n" +
"The API implements rate limiting to ensure fair usage:\n\n" +
"- Authentication endpoints: 10 requests per minute\n" +
"- Data submission endpoints: 60 requests per minute\n" +
"- Report retrieval endpoints: 120 requests per minute\n\n" +
"When rate limits are exceeded, the API returns a 429 Too Many Requests status code with a Retry-After header indicating when you can resume requests.\n\n" +
"## Example Code\n\n" +
"### Python Example\n\n" +
"```python\n" +
"#!/usr/bin/env python3\n" +
"\"\"\"Vendor API Integration Example\n\n" +
"This script demonstrates how to integrate with the ReasonShield Vendor API.\n" +
"It shows how to:\n" +
"1. Submit data to the vendor API\n" +
"2. Check the processing status of submitted data\n" +
"3. Retrieve generated reports\n" +
"4. Handle errors and authentication\n\n" +
"For vendor onboarding and integration support, please contact support@reasonshield.com\n" +
"\"\"\"\n\n" +
"import os\n" +
"import sys\n" +
"import json\n" +
"import hmac\n" +
"import hashlib\n" +
"import uuid\n" +
"import time\n" +
"import requests\n" +
"import logging\n" +
"from datetime import datetime\n" +
"from typing import Dict, Any, Optional, List\n\n" +
"# Configure logging\n" +
"logging.basicConfig(\n" +
"    level=logging.INFO,\n" +
"    format='%(asctime)s - %(levelname)s - %(message)s',\n" +
"    handlers=[\n" +
"        logging.FileHandler(\"vendor_api.log\", encoding=\"utf-8\"),\n" +
"        logging.StreamHandler(sys.stdout)\n" +
"    ]\n" +
")\n" +
"logger = logging.getLogger(__name__)\n\n" +
"# MAJOR CONSTANTS\n" +
"API_BASE_URL = os.getenv(\"API_BASE_URL\", \"https://app.reasonshield.com/api/v1\")\n" +
"API_ENDPOINT_PATH = \"/lis-webhook/vendor\"\n" +
"OUTPUT_DIR = \"vendor_api_output\"\n\n" +
"# Your assigned vendor credentials\n" +
"# Note: Replace these with your actual credentials from the vendor portal\n" +
"VENDOR_ID = os.getenv(\"VENDOR_ID\", \"your-vendor-id-here\")\n" +
"API_KEY = os.getenv(\"VENDOR_API_KEY\", \"your-api-key-here\")\n" +
"WEBHOOK_SECRET = os.getenv(\"VENDOR_WEBHOOK_SECRET\", \"your-webhook-secret-here\")\n\n" +
"# Processing constants\n" +
"MAX_RETRIES = 30\n" +
"RETRY_DELAY = 2  # seconds\n\n" +
"# Create output directory if it doesn't exist\n" +
"os.makedirs(OUTPUT_DIR, exist_ok=True)\n\n" +
"def generate_signature(payload: Dict[str, Any], secret: str) -> str:\n" +
"    \"\"\"\n" +
"    Generate HMAC signature for the payload.\n" +
"    \n" +
"    Args:\n" +
"        payload: The data to be signed\n" +
"        secret: The secret key used for signing\n" +
"        \n" +
"    Returns:\n" +
"        str: The HMAC signature\n" +
"    \"\"\"\n" +
"    try:\n" +
"        payload_bytes = json.dumps(payload).encode('utf-8')\n" +
"        signature = hmac.new(\n" +
"            secret.encode('utf-8'),\n" +
"            payload_bytes,\n" +
"            hashlib.sha256\n" +
"        ).hexdigest()\n" +
"        return signature\n" +
"    except Exception as e:\n" +
"        logger.error(f\"Error generating signature: {str(e)}\")\n" +
"        raise\n\n" +
"def create_sample_data(vendor_id: str) -> Dict[str, Any]:\n" +
"    \"\"\"\n" +
"    Create sample data for submission to the vendor API.\n" +
"    \n" +
"    Args:\n" +
"        vendor_id: Your assigned vendor ID\n" +
"    \n" +
"    Returns:\n" +
"        Dict: Sample data payload\n" +
"    \"\"\"\n" +
"    # Create test data with recommended structure\n" +
"    return {\n" +
"        \"vendor_id\": vendor_id,\n" +
"        \"timestamp\": datetime.now().isoformat(),\n" +
"        \"request_id\": str(uuid.uuid4()),\n" +
"        \"test_data\": {\n" +
"            \"test_id\": \"159809\",                      # Required: Unique identifier for the test\n" +
"            \"panel_type\": \"RESPIRATORY\",                   # Required: Use uppercase (RESPIRATORY, UTI, GI, etc.)\n" +
"            \"panel_size\": 56,                              # Required: Number of tests in the panel\n" +
"            \"patient_info\": {                              # Required: Patient information\n" +
"                \"patient_id\": \"56012\",                       # Required: Patient ID\n" +
"                \"patient_name\": \"Mcdaniel, Garrick\",         # Required: Patient name\n" +
"                \"patient_dob\": \"1995-01-06\",                 # Required: ISO format date (YYYY-MM-DD)\n" +
"                \"patient_gender\": \"Male\",                    # Required: Male, Female, or Other\n" +
"                \"patient_age\": \"29 years\"                    # Optional: Patient age\n" +
"            },\n" +
"            \"collection_info\": {                           # Required: Collection information\n" +
"                \"collection_site\": \"NASO\",                   # Required: Site code\n" +
"                \"specimen_type\": \"Nasopharyngeal swab\",      # Required: Type of specimen\n" +
"                \"collection_date\": \"2024-09-10T13:11:00-06:00\", # Required: ISO datetime\n" +
"                \"received_date\": \"2024-09-11T10:55:00-06:00\"    # Required: ISO datetime\n" +
"            },\n" +
"            \"provider_info\": {                             # Required: Provider information\n" +
"                \"provider_name\": \"Patel, Satish\",            # Required: Provider name\n" +
"                \"provider_location\": \"Main Clinic\"           # Optional: Provider location\n" +
"            },\n" +
"            \"test_details\": {                              # Required: Test details\n" +
"                \"viruses\": [                                 # Optional: Viral test results\n" +
"                {  \n" +
"                    \"name\": \"SARS-CoV-2 (N gene)\",           # Required: Pathogen name\n" +
"                    \"result\": \"Not Detected\"                 # Required: \"Detected\" or \"Not Detected\"\n" +
"                }\n" +
"                ],\n" +
"                \"bacteria\": [                                # Optional: Bacterial test results\n" +
"                {\n" +
"                    \"name\": \"Bordetella pertussis\",          # Required: Pathogen name\n" +
"                    \"result\": \"Not Detected\"                 # Required: \"Detected\" or \"Not Detected\"\n" +
"                }\n" +
"                ],\n" +
"                \"resistance_genes\": [                        # Optional: Resistance gene results\n" +
"                {\n" +
"                    \"name\": \"beta-lactamase CTX-M\",          # Required: Gene name\n" +
"                    \"result\": \"Detected\"                     # Required: \"Detected\" or \"Not Detected\"\n" +
"                }\n" +
"                ],\n" +
"                \"fungi\": [                                   # Optional: Fungal test results\n" +
"                {\n" +
"                    \"name\": \"Candida albicans\",              # Required: Pathogen name\n" +
"                    \"result\": \"Not Detected\"                 # Required: \"Detected\" or \"Not Detected\"\n" +
"                }\n" +
"                ]\n" +
"            },\n" +
"            \"metadata\": {                                  # Optional: Additional metadata\n" +
"                \"report_status\": \"Final\",                    # Optional: Status (Preliminary, Final, etc.)\n" +
"                \"report_date\": \"2024-09-12T09:18:00-06:00\",  # Optional: ISO datetime\n" +
"                \"performing_lab\": \"Main Laboratory\",         # Optional: Name of performing lab\n" +
"                \"test_method\": \"PCR\"                         # Optional: Test method\n" +
"            }\n" +
"        }\n" +
"    }\n\n" +
"def submit_data(\n" +
"    data: Dict[str, Any],\n" +
"    use_api_key: bool = True,\n" +
"    use_signature: bool = False\n" +
") -> Dict[str, Any]:\n" +
"    \"\"\"\n" +
"    Submit data to the vendor API.\n" +
"    \n" +
"    Args:\n" +
"        data: The data to submit\n" +
"        use_api_key: Whether to use API key authentication\n" +
"        use_signature: Whether to use webhook signature authentication\n" +
"        \n" +
"    Returns:\n" +
"        Dict: The response from the API\n" +
"    \"\"\"\n" +
"    # Setup headers\n" +
"    headers = {\n" +
"        \"Content-Type\": \"application/json\"\n" +
"    }\n" +
"    \n" +
"    # Add authentication - API key is recommended for most integrations\n" +
"    if use_api_key:\n" +
"        headers[\"Authorization\"] = f\"Bearer {API_KEY}\"\n" +
"    \n" +
"    # Webhook signature is an alternative authentication method\n" +
"    if use_signature:\n" +
"        signature = generate_signature(data, WEBHOOK_SECRET)\n" +
"        headers[\"X-Vendor-Signature\"] = signature\n" +
"    \n" +
"    # Construct the full API URL\n" +
"    url = f\"{API_BASE_URL}{API_ENDPOINT_PATH}/submit-data\"\n" +
"    \n" +
"    # Send the request\n" +
"    try:\n" +
"        logger.info(f\"Submitting data to vendor API with vendor_id: {data['vendor_id']}\")\n" +
"        response = requests.post(\n" +
"            url,\n" +
"            json=data,\n" +
"            headers=headers\n" +
"        )\n" +
"        \n" +
"        # Check response\n" +
"        if response.status_code == 202:\n" +
"            logger.info(f\"Data submitted successfully. Status code: {response.status_code}\")\n" +
"            return response.json()\n" +
"        else:\n" +
"            logger.error(f\"Error submitting data. Status code: {response.status_code}\")\n" +
"            logger.error(f\"Response: {response.text}\")\n" +
"            raise Exception(f\"Error submitting data: {response.text}\")\n" +
"    \n" +
"    except requests.exceptions.RequestException as e:\n" +
"        logger.error(f\"Request exception during data submission: {str(e)}\")\n" +
"        raise\n" +
"    except Exception as e:\n" +
"        logger.error(f\"Exception during data submission: {str(e)}\")\n" +
"        raise\n\n" +
"def check_status(data_id: str) -> Dict[str, Any]:\n" +
"    \"\"\"\n" +
"    Check the processing status of submitted data.\n" +
"    \n" +
"    Args:\n" +
"        data_id: The ID of the submitted data\n" +
"        \n" +
"    Returns:\n" +
"        Dict: The status information\n" +
"    \"\"\"\n" +
"    headers = {\n" +
"        \"Authorization\": f\"Bearer {API_KEY}\"\n" +
"    }\n" +
"    \n" +
"    url = f\"{API_BASE_URL}{API_ENDPOINT_PATH}/status/{data_id}\"\n" +
"    \n" +
"    try:\n" +
"        logger.info(f\"Checking processing status for data_id: {data_id}\")\n" +
"        response = requests.get(\n" +
"            url,\n" +
"            headers=headers\n" +
"        )\n" +
"        \n" +
"        # Check response\n" +
"        if response.status_code == 200:\n" +
"            status_data = response.json()\n" +
"            logger.info(f\"Status check successful. Status: {status_data.get('status', 'unknown')}\")\n" +
"            return status_data\n" +
"        else:\n" +
"            logger.error(f\"Error checking status. Status code: {response.status_code}\")\n" +
"            logger.error(f\"Response: {response.text}\")\n" +
"            raise Exception(f\"Error checking status: {response.text}\")\n" +
"    \n" +
"    except requests.exceptions.RequestException as e:\n" +
"        logger.error(f\"Request exception during status check: {str(e)}\")\n" +
"        raise\n" +
"    except Exception as e:\n" +
"        logger.error(f\"Exception during status check: {str(e)}\")\n" +
"        raise\n\n" +
"def get_report(data_id: str) -> Optional[Dict[str, Any]]:\n" +
"    \"\"\"\n" +
"    Retrieve the generated report for submitted data.\n" +
"    \n" +
"    Args:\n" +
"        data_id: The ID of the submitted data\n" +
"        \n" +
"    Returns:\n" +
"        Dict or None: The report data if available, None if still processing\n" +
"    \"\"\"\n" +
"    headers = {\n" +
"        \"Authorization\": f\"Bearer {API_KEY}\"\n" +
"    }\n" +
"    \n" +
"    url = f\"{API_BASE_URL}{API_ENDPOINT_PATH}/report/{data_id}\"\n" +
"    \n" +
"    try:\n" +
"        logger.info(f\"Retrieving report for data_id: {data_id}\")\n" +
"        response = requests.get(\n" +
"            url,\n" +
"            headers=headers\n" +
"        )\n" +
"        \n" +
"        # Check response\n" +
"        if response.status_code == 200:\n" +
"            logger.info(\"Report retrieved successfully\")\n" +
"            return response.json()\n" +
"        elif response.status_code == 202:\n" +
"            logger.info(\"Report is still being generated\")\n" +
"            return None\n" +
"        else:\n" +
"            logger.error(f\"Error retrieving report. Status code: {response.status_code}\")\n" +
"            logger.error(f\"Response: {response.text}\")\n" +
"            raise Exception(f\"Error retrieving report: {response.text}\")\n" +
"    \n" +
"    except requests.exceptions.RequestException as e:\n" +
"        logger.error(f\"Request exception during report retrieval: {str(e)}\")\n" +
"        raise\n" +
"    except Exception as e:\n" +
"        logger.error(f\"Exception during report retrieval: {str(e)}\")\n" +
"        raise\n\n" +
"def download_pdf_report(pdf_url: str, data_id: str) -> str:\n" +
"    \"\"\"\n" +
"    Download a PDF report from the provided URL.\n" +
"    \n" +
"    Args:\n" +
"        pdf_url: The URL to download the PDF from\n" +
"        data_id: The ID of the data for naming the file\n" +
"        \n" +
"    Returns:\n" +
"        str: The path to the downloaded file\n" +
"    \"\"\"\n" +
"    headers = {\n" +
"        \"Authorization\": f\"Bearer {API_KEY}\"\n" +
"    }\n" +
"    \n" +
"    # If pdf_url is a relative URL, make it absolute\n" +
"    if pdf_url.startswith('/'):\n" +
"        # Extract the base part (e.g., https://example.com) from API_BASE_URL\n" +
"        from urllib.parse import urlparse\n" +
"        parsed_url = urlparse(API_BASE_URL)\n" +
"        base_url = f\"{parsed_url.scheme}://{parsed_url.netloc}\"\n" +
"        pdf_url = f\"{base_url}{pdf_url}\"\n" +
"    \n" +
"    try:\n" +
"        logger.info(f\"Downloading PDF report from: {pdf_url}\")\n" +
"        response = requests.get(\n" +
"            pdf_url,\n" +
"            headers=headers,\n" +
"            stream=True  # Stream the download for large files\n" +
"        )\n" +
"        \n" +
"        # Check response\n" +
"        if response.status_code == 200:\n" +
"            timestamp = datetime.now().strftime(\"%Y%m%d_%H%M%S\")\n" +
"            filename = f\"{OUTPUT_DIR}/vendor_report_{data_id}_{timestamp}.pdf\"\n" +
"            \n" +
"            # Save the PDF to a file\n" +
"            with open(filename, 'wb') as f:\n" +
"                for chunk in response.iter_content(chunk_size=8192):\n" +
"                    f.write(chunk)\n" +
"            \n" +
"            logger.info(f\"PDF report downloaded to {filename}\")\n" +
"            return filename\n" +
"        else:\n" +
"            logger.error(f\"Error downloading PDF. Status code: {response.status_code}\")\n" +
"            logger.error(f\"Response: {response.text}\")\n" +
"            raise Exception(f\"Error downloading PDF: {response.text}\")\n" +
"    \n" +
"    except requests.exceptions.RequestException as e:\n" +
"        logger.error(f\"Request exception during PDF download: {str(e)}\")\n" +
"        raise\n" +
"    except Exception as e:\n" +
"        logger.error(f\"Exception during PDF download: {str(e)}\")\n" +
"        raise\n" +
"```\n\n" +
"For more detailed examples in different programming languages, please refer to our [API Examples](/lis-portal/examples) section.";

const VendorApiGuidePage: React.FC = () => {

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <Suspense fallback={<div className="w-full h-24 flex items-center justify-center">Loading documentation...</div>}>
        <div className={`
          prose prose-lg max-w-none
          prose-headings:text-primary prose-headings:mt-6 prose-headings:mb-3
          prose-h1:text-3xl prose-h1:border-b prose-h1:border-border prose-h1:pb-2
          prose-h2:text-2xl
          prose-h3:text-xl
          prose-h4:text-lg
          prose-p:mb-4
          prose-a:text-primary
          prose-blockquote:border-l-4 prose-blockquote:border-primary-light prose-blockquote:pl-4 prose-blockquote:my-4 prose-blockquote:text-text-secondary
          prose-table:w-full prose-table:border-collapse prose-table:mb-6
          prose-th:border prose-th:border-border prose-th:p-2 prose-th:bg-background-alt
          prose-td:border prose-td:border-border prose-td:p-2
          prose-ul:pl-6 prose-ul:mb-4
          prose-ol:pl-6 prose-ol:mb-4
          prose-li:mb-2
          prose-pre:rounded prose-pre:overflow-auto prose-pre:mb-4
          prose-code:font-mono prose-code:text-sm
          prose-img:max-w-full prose-img:h-auto
        `}>
          <ReactMarkdown
            components={{
              code({node, inline, className, children, ...props}) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={atomDark as any}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
            }}
          >
            {GUIDE_CONTENT}
          </ReactMarkdown>
        </div>
      </Suspense>
    </div>
  );
};

export default VendorApiGuidePage;